import { createContext, type ReactNode, useContext, useState } from 'react'

const CockpitCardCornerSlotContext = createContext<HTMLElement | null>(null)

interface ICockpitCardCornerSlotProviderProps {
  /** The node the corner is drawn into; `null` until the card has laid its header out. */
  container: HTMLElement | null
  children: ReactNode
}

/**
 * Hands every part of an expanded card the same corner to draw its controls into.
 *
 * A card's controls are known by the section that owns their handlers, their blocked states and
 * their dialogs, while where they are drawn belongs to the card. The slot keeps the two apart, so
 * one corner serves whatever section is on screen.
 */
export const CockpitCardCornerSlotProvider = ({
  container,
  children,
}: ICockpitCardCornerSlotProviderProps) => (
  <CockpitCardCornerSlotContext.Provider value={container}>
    {children}
  </CockpitCardCornerSlotContext.Provider>
)

CockpitCardCornerSlotProvider.displayName = 'CockpitCardCornerSlotProvider'

/** The corner of the card being drawn, or `null` for anything rendered outside one. */
export const useCockpitCardCornerSlot = () => useContext(CockpitCardCornerSlotContext)

/**
 * The card's own side of the slot: the node to hang in the header, and the value to hand the
 * provider. It is state rather than a ref — the controls are rendered by somebody else, and they
 * have to render again once the node exists.
 */
export const useCockpitCardCornerHost = () => {
  const [container, setContainer] = useState<HTMLElement | null>(null)

  return { container, setContainer }
}
